"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="204437" className="flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-mono text-[13px] uppercase tracking-[2px] text-foreground hover:text-primary transition-colors duration-300">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-8 mt-16 px-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-40 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <div className="flex flex-col gap-8 mt-16 px-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <SheetClose asChild>
            <Link to={item.href || "#"} className="font-mono text-3xl uppercase tracking-[2px] text-foreground hover:text-primary transition-colors duration-300 block">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
    </div>;
}
export default function TrendMitraNavigationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="global-navigation" className={cn("dark text-foreground sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent")}>
      <header className="max-w-7xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634070/img/trendmitra_logo.png" alt="WorldAccess" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="22">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center justify-end gap-4 lg:gap-6 flex-shrink-0">
          <div className="hidden lg:block w-48">
            <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search services..." className="[&_input]:bg-transparent [&_input]:border-border [&_input]:text-foreground [&_input]:focus-visible:ring-1 [&_input]:focus-visible:ring-primary [&_input]:transition-colors" />
          </div>

          <CartIcon variant="ghost" iconSize="size-5" className="text-foreground hover:text-primary transition-colors hover:bg-transparent" />

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[400px] bg-background border-l border-border p-0">
              <div className="p-6 border-b border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634070/img/trendmitra_logo.png" alt="WorldAccess" className="h-7 w-auto object-contain" />
              </div>
              <MenuProvider menu_id="22">
                <MobileMenuConsumer />
              </MenuProvider>
              <div className="mt-auto p-6">
                <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search services..." className="w-full [&_input]:bg-muted/10 [&_input]:border-border [&_input]:text-foreground" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </section>;
}